import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  SxProps,
  Theme,
  Switch,
  Avatar,
  Typography,
  Stack,
  CircularProgress,
  Box,
  Button,
} from '@mui/material';
import { neutral, primary } from '_lex/_foundations/phxColors';
import PhxBadge from '_lex/_atoms/PhxBadge';
import { useManageSolutionsContext } from '../context';
import InactiveState from '../List/components/InactiveState';
import { translate as t } from 'hooks/useTranslations';

type SolutionCardProps = {
  solutionKey: string;
  title: string;
  view: React.ReactNode;
  edit: React.ReactNode;
  onSave?: () => Promise<void> | void;
  onCancel?: () => void;
  sx?: SxProps<Theme>;
  isLoading?: boolean;
};

const SolutionCard: React.FC<SolutionCardProps> = ({
  solutionKey,
  title,
  view,
  edit,
  onSave,
  onCancel,
  sx,
  isLoading = false,
}) => {
  const { getIcon, isSolutionActive, toggleSolutionByKey, uiIsSaving } = useManageSolutionsContext();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  const isActive = isSolutionActive(solutionKey);

  // Handle edit button click
  const handleEditClick = () => {
    setIsEditing(true);
  };

  // Handle save button click
  const handleSaveClick = async () => {
    if (!onSave) return;

    setIsSaveLoading(true);
    try {
      await onSave();
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setIsSaveLoading(false);
    }
  };

  // Handle cancel button click
  const handleCancelClick = () => {
    setIsEditing(false);
    if (onCancel) {
      onCancel();
    }
  };

  // Handle primary button click (Edit Settings / Save / Done)
  const handlePrimaryButtonClick = async () => {
    if (!isEditing) {
      handleEditClick();
      return;
    }

    if (isActive) {
      await handleSaveClick();
    } else {
      handleCancelClick();
    }
  };

  // Handle solution toggle (active/inactive)
  const handleSolutionToggle = async (checked: boolean) => {
    await toggleSolutionByKey(solutionKey, checked);
  };

  const headerActions = isEditing ? (
    <Stack
      direction='row'
      alignItems='center'
      spacing={1}
    >
      <Switch
        checked={isActive}
        onChange={(_, checked) => handleSolutionToggle(checked)}
        color='success'
        disabled={uiIsSaving || isLoading}
        size='small'
      />
      {(uiIsSaving || isLoading) && <CircularProgress size={16} />}
    </Stack>
  ) : (
    <PhxBadge
      color={isActive ? 'success' : 'default'}
      size='sm'
    >
      {isActive ? 'Active' : 'Inactive'}
    </PhxBadge>
  );

  return (
    <Card
      className='solution-card'
      sx={{
        mt: 6,
        mx: 'auto',
        width: '100%',
        maxWidth: 800,
        ...sx,
      }}
    >
      <CardHeader
        avatar={
          getIcon(solutionKey) ? (
            <Avatar
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                border: '1px solid',
                borderColor: neutral[200],
                bgcolor: neutral[25],
                color: primary[800],
                '& svg': { fontSize: 24 },
              }}
            >
              {getIcon(solutionKey)}
            </Avatar>
          ) : undefined
        }
        title={
          <Typography
            variant='subtitleMd'
            sx={{
              color: neutral[700],
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </Typography>
        }
        action={headerActions}
        sx={{
          px: 6,
          py: 4,
          borderBottom: '1px solid',
          borderColor: neutral[100],
          backgroundColor: neutral[25],
          '& .MuiCardHeader-action': {
            alignSelf: 'center',
            margin: 0,
          },
        }}
      />

      <CardContent sx={{ p: 8 }}>
        {isLoading ? (
          <Box sx={{ justifyContent: 'center', display: 'flex', minHeight: '20vh', alignItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : !isActive ? (
          <InactiveState />
        ) : isEditing ? (
          edit
        ) : (
          view
        )}
      </CardContent>

      {onSave && (
        <CardActions
          sx={{
            px: 4,
            py: 4,
            borderTop: '1px solid',
            borderColor: neutral[100],
            backgroundColor: neutral[25],
            justifyContent: 'space-between',
          }}
        >
          <Stack
            direction='row'
            alignItems='center'
            spacing={2}
          >
            {isEditing && (
              <Button
                variant='outlined'
                color='neutral'
                onClick={handleCancelClick}
                disabled={isSaveLoading || uiIsSaving}
              >
                {t('common.cancel')}
              </Button>
            )}
          </Stack>
          <Stack
            direction='row'
            alignItems='center'
            spacing={2}
          >
            <Button
              variant='contained'
              onClick={handlePrimaryButtonClick}
              endIcon={isSaveLoading ? <CircularProgress size={16} /> : null}
              disabled={isSaveLoading || uiIsSaving}
            >
              {isEditing ? (isActive ? t('common.save') : t('common.done')) : t('manageSolutions.editSettings')}
            </Button>
          </Stack>
        </CardActions>
      )}
    </Card>
  );
};

export default SolutionCard;
